import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from "react-redux"
import Select from 'react-select'

import PeriodPicker from 'pages/parts/PeriodPicker'
import PageTabs from 'pages/parts/PageTabs'
import Pagination from 'pages/parts/Pagination'
import XLSX from 'xlsx-js-style'
import { periodText } from 'pages/main/chain/parts/chainExcel'

import { GET, httpOk } from 'helpers/api'
import { formatDate, formatMoney } from "helpers/helpers"

const PAGE_SIZE = 50
const STATUS_COLORS = { accepted: '#198754', expected: '#d99a00', deleted: '#dc3545' }

function statusKind(status) {
	if (status === 1) return 'accepted'
	if (status < 0 && status !== -2) return 'deleted'
	return 'expected'
}

// Отчёт «Приход от внешнего поставщика по продуктам»: сколько, на какую сумму и когда отправил поставщик.
// Продуктов можно выбрать несколько; пусто — все продукты.
function Index() {
	const { t } = useTranslation()
	const date = new Date()
	const reduxAccount = useSelector(state => state.account)

	const [filterData, setFilterData] = useState({
		start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
		end_date: formatDate(new Date(), 'yyyy-MM-dd'),
		pos_id: reduxAccount.pos_id,
		supplier_id: null,
		document_status: null,
		product_names: [],
	})
	const [suppliers, setSuppliers] = useState([])
	const [documentStatuses, setDocumentStatuses] = useState([])
	const [productNames, setProductNames] = useState([])
	const [report, setReport] = useState(null)
	const [tab, setTab] = useState('summary')
	const [page, setPage] = useState(0)

	async function getReport() {
		const params = new URLSearchParams()
		params.append('pos_id', filterData.pos_id)
		params.append('start_date', filterData.start_date)
		params.append('end_date', filterData.end_date)
		if (filterData.supplier_id) params.append('supplier_id', filterData.supplier_id)
		if (filterData.document_status) params.append('document_status', filterData.document_status)
		filterData.product_names.forEach(name => params.append('product_names[]', name))

		const response = await GET(`/service/documents-in-products-report?${params.toString()}`, {}, { loader: true })
		if (httpOk(response)) {
			setReport(response.data)
			setPage(0)
		}
	}

	useEffect(() => {
		(async () => {
			const [suppliersResponse, statusesResponse, productsResponse] = await Promise.all([
				GET('/helper/suppliers'),
				GET('/helper/document-statuses?entity=wms_transfer_pos'),
				GET(`/service/documents-in-products?pos_id=${filterData.pos_id}`),
			])
			if (httpOk(suppliersResponse)) setSuppliers(suppliersResponse.data)
			if (httpOk(statusesResponse)) setDocumentStatuses(statusesResponse.data.filter(item => item.value !== 'draft'))
			if (httpOk(productsResponse)) setProductNames(productsResponse.data)
		})()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		getReport()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const totals = (report?.summary || []).reduce((sum, row) => {
		Object.keys(sum).forEach(key => { sum[key] += Number(row[key]) || 0 })
		return sum
	}, { quantity: 0, amount: 0, accepted_quantity: 0, accepted_amount: 0, pending_quantity: 0, pending_amount: 0 })

	// Excel: два листа (продукты и документы), шапка заливкой, рамки, итог жирным
	function exportExcel() {
		if (!report) return
		const thin = { style: 'thin', color: { rgb: '999999' } }
		const borders = { top: thin, bottom: thin, left: thin, right: thin }
		const headStyle = { font: { bold: true }, fill: { fgColor: { rgb: 'E9EDF5' } }, border: borders, alignment: { horizontal: 'center', vertical: 'center', wrapText: true } }
		const cellStyle = (align, bold, color) => ({
			font: { bold: !!bold, color: color ? { rgb: color } : undefined },
			fill: bold ? { fgColor: { rgb: 'F3F5F9' } } : undefined,
			border: borders,
			alignment: { horizontal: align, vertical: 'center' },
		})
		const statusRgb = {}
		Object.keys(STATUS_COLORS).forEach(kind => { statusRgb[t(kind)] = STATUS_COLORS[kind].replace('#', '') })
		const qtyFormat = '#,##0.###'
		const moneyFormat = '#,##0'

		const supplierName = suppliers.find(item => item.id === filterData.supplier_id)?.name
		const title = `${t('receipt_from_supplier')} — ${t('products')}`
		const subtitle = [
			`${t('period')}: ${periodText(filterData.start_date, filterData.end_date)}`,
			supplierName ? `${t('supplier')}: ${supplierName}` : '',
		].filter(Boolean).join('   ')

		// column: { label, width, align, format, color(row) }; rows — массивы значений; footer — необязательная строка итога
		function makeSheet(columns, rows, footer) {
			const aoa = [
				[{ v: title, t: 's', s: { font: { bold: true, sz: 14 } } }],
				[{ v: subtitle, t: 's' }],
				[],
				columns.map(column => ({ v: column.label, t: 's', s: headStyle })),
			]
			const toCell = (value, column, bold) => {
				const isNumber = column.format && value !== '' && value !== null && value !== undefined
				return {
					v: isNumber ? Number(value) : (value ?? ''),
					t: isNumber ? 'n' : 's',
					z: isNumber ? column.format : undefined,
					s: cellStyle(column.align, bold, column.color ? column.color(value) : undefined),
				}
			}
			rows.forEach(row => aoa.push(row.map((value, index) => toCell(value, columns[index], false))))
			if (footer) aoa.push(footer.map((value, index) => toCell(value, { ...columns[index], color: null }, true)))

			const sheet = XLSX.utils.aoa_to_sheet(aoa)
			sheet['!cols'] = columns.map(column => ({ wch: column.width }))
			sheet['!merges'] = [
				{ s: { r: 0, c: 0 }, e: { r: 0, c: columns.length - 1 } },
				{ s: { r: 1, c: 0 }, e: { r: 1, c: columns.length - 1 } },
			]
			return sheet
		}

		const summarySheet = makeSheet([
			{ label: '#', width: 6, align: 'center' },
			{ label: t('product'), width: 38, align: 'left' },
			{ label: t('unit'), width: 9, align: 'center' },
			{ label: t('sent'), width: 14, align: 'right', format: qtyFormat },
			{ label: `${t('sent')}, ${t('amount')}`, width: 16, align: 'right', format: moneyFormat },
			{ label: t('accepted'), width: 14, align: 'right', format: qtyFormat },
			{ label: `${t('accepted')}, ${t('amount')}`, width: 16, align: 'right', format: moneyFormat },
			{ label: t('expected'), width: 14, align: 'right', format: qtyFormat },
			{ label: `${t('expected')}, ${t('amount')}`, width: 16, align: 'right', format: moneyFormat },
		], report.summary.map((row, index) => [
			index + 1, row.product_name, row.unit_name,
			row.quantity, row.amount, row.accepted_quantity, row.accepted_amount, row.pending_quantity, row.pending_amount,
		]), [
			'', t('total'), '',
			totals.quantity, totals.amount, totals.accepted_quantity, totals.accepted_amount, totals.pending_quantity, totals.pending_amount,
		])

		const linesAmount = report.lines.reduce((sum, line) => sum + Number(line.amount || 0), 0)
		const linesSheet = makeSheet([
			{ label: '#', width: 6, align: 'center' },
			{ label: t('created'), width: 18, align: 'center' },
			{ label: t('supplier'), width: 28, align: 'left' },
			{ label: t('product'), width: 38, align: 'left' },
			{ label: t('unit'), width: 9, align: 'center' },
			{ label: t('quantity'), width: 14, align: 'right', format: qtyFormat },
			{ label: t('price'), width: 14, align: 'right', format: moneyFormat },
			{ label: t('amount'), width: 16, align: 'right', format: moneyFormat },
			{ label: t('status'), width: 14, align: 'center', color: (value) => statusRgb[value] },
		], report.lines.map((line, index) => [
			index + 1, formatDate(line.created_at, 'dd.MM.yyyy HH:mm'), line.supplier_name, line.product_name, line.unit_name,
			line.quantity, line.price, line.amount, t(statusKind(line.status)),
		]), ['', t('total'), '', '', '', '', '', linesAmount, ''])

		const workbook = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(workbook, summarySheet, t('products').slice(0, 31))
		XLSX.utils.book_append_sheet(workbook, linesSheet, t('document').slice(0, 31))
		XLSX.writeFile(workbook, `${title} ${filterData.start_date} ${filterData.end_date}.xlsx`)
	}

	return (
		<div>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('receipt_from_supplier')} — {t('products')}
				</h4>
				<div className="d-flex gap-2 align-items-center">
					<button className="btn btn-outline-success" onClick={exportExcel} disabled={!report || report.lines.length === 0}>
						{t('download')} Excel
					</button>
					<button className="btn btn-primary" onClick={getReport}>{t('apply')}</button>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="row g-2 report-filters">
						<div className="form-group col-md-2">
							<label>{t('suppliers')}</label>
							<Select
								isClearable
								options={suppliers}
								value={suppliers.find(option => option.id === filterData.supplier_id) || null}
								onChange={(option) => setFilterData({ ...filterData, supplier_id: option ? option.id : null })}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
						<div className="form-group col-md-4">
							<label>{t('products')}</label>
							<Select
								isMulti
								closeMenuOnSelect={false}
								options={productNames}
								value={productNames.filter(option => filterData.product_names.includes(option.name))}
								onChange={(options) => setFilterData({ ...filterData, product_names: (options || []).map(option => option.name) })}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.name}
							/>
						</div>
						<div className="form-group col-md-4">
							<label>{t('period')}</label>
							<PeriodPicker
								startDate={filterData.start_date}
								endDate={filterData.end_date}
								onChange={(period) => setFilterData({ ...filterData, ...period })}
								withTime={false}
								minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
								maxDate={new Date(new Date().getFullYear(), 11, 31)}
							/>
						</div>
						<div className="form-group col-md-2">
							<label>{t('status')}</label>
							<Select
								isClearable
								options={documentStatuses}
								value={documentStatuses.find(option => option.value === filterData.document_status) || null}
								onChange={(option) => setFilterData({ ...filterData, document_status: option ? option.value : null })}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => t(option.value)}
								getOptionValue={(option) => option.value}
							/>
						</div>
					</div>

					{report && report.lines.length === 0 &&
						<div className="text-center text-muted py-5">{t('list_empty')}</div>
					}

					{report && report.lines.length > 0 &&
						<div className="mt-3">
							<PageTabs
								active={tab}
								onChange={(key) => { setTab(key); setPage(0) }}
								tabs={[
									{ key: 'summary', label: `${t('products')} [${report.summary.length}]` },
									{ key: 'lines', label: `${t('document')} [${report.lines.length}]` },
								]}
							/>

							{report.truncated &&
								<div className="text-danger mb-2">{t('documents_in_products_report_truncated')}</div>
							}

							{tab === 'summary' &&
								<>
									<div className="table-responsive">
										<table className="table table-bordered table-hover mb-0">
											<thead>
												<tr>
													<th className="text-center" style={{ width: '50px' }}>#</th>
													<th>{t('product')}</th>
													<th className="text-center">{t('unit')}</th>
													<th className="text-end">{t('sent')}</th>
													<th className="text-end" style={{ color: STATUS_COLORS.accepted }}>{t('accepted')}</th>
													<th className="text-end" style={{ color: STATUS_COLORS.expected }}>{t('expected')}</th>
													<th className="text-end">{t('amount')}</th>
												</tr>
											</thead>
											<tbody>
												{report.summary.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((row, index) => (
													<tr key={row.product_name}>
														<td className="text-center">{page * PAGE_SIZE + index + 1}</td>
														<td>{row.product_name}</td>
														<td className="text-center">{row.unit_name}</td>
														<td className="text-end text-nowrap"><b>{formatMoney(row.quantity, 3)}</b></td>
														<td className="text-end text-nowrap">{formatMoney(row.accepted_quantity, 3)}</td>
														<td className="text-end text-nowrap">{formatMoney(row.pending_quantity, 3)}</td>
														<td className="text-end text-nowrap">{formatMoney(row.amount)}</td>
													</tr>
												))}
											</tbody>
											{report.summary.length > 1 &&
												<tfoot>
													<tr className="fw-bold">
														<td colSpan={6}>{t('total')}</td>
														<td className="text-end text-nowrap">{formatMoney(totals.amount)}</td>
													</tr>
												</tfoot>
											}
										</table>
									</div>
									<div className="mt-3">
										<Pagination pageCount={Math.ceil(report.summary.length / PAGE_SIZE)} paginate={(e) => setPage(e.selected)} />
									</div>
								</>
							}

							{tab === 'lines' &&
								<>
									<div className="table-responsive">
										<table className="table table-bordered table-hover mb-0">
											<thead>
												<tr>
													<th className="text-center" style={{ width: '50px' }}>#</th>
													<th className="text-center" style={{ width: '14%' }}>{t('created')}</th>
													<th style={{ width: '20%' }}>{t('supplier')}</th>
													<th>{t('product')}</th>
													<th className="text-end" style={{ width: '10%' }}>{t('quantity')}</th>
													<th className="text-end" style={{ width: '10%' }}>{t('price')}</th>
													<th className="text-end" style={{ width: '12%' }}>{t('amount')}</th>
													<th className="text-center" style={{ width: '10%' }}>{t('status')}</th>
												</tr>
											</thead>
											<tbody>
												{report.lines.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((line, index) => (
													<tr key={`${line.document_id}-${page * PAGE_SIZE + index}`}>
														<td className="text-center">{page * PAGE_SIZE + index + 1}</td>
														<td className="text-center text-nowrap">{formatDate(line.created_at, 'dd.MM.yy HH:mm')}</td>
														<td className="td-ellipsis">{line.supplier_name}</td>
														<td>{line.product_name}</td>
														<td className="text-end text-nowrap">{formatMoney(line.quantity, 3)} {line.unit_name}</td>
														<td className="text-end text-nowrap">{formatMoney(line.price)}</td>
														<td className="text-end text-nowrap">{formatMoney(line.amount)}</td>
														<td className="text-center text-nowrap">
															<span style={{ color: STATUS_COLORS[statusKind(line.status)], fontWeight: 600 }}>{t(statusKind(line.status))}</span>
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
									<div className="mt-3">
										<Pagination pageCount={Math.ceil(report.lines.length / PAGE_SIZE)} paginate={(e) => setPage(e.selected)} />
									</div>
								</>
							}
						</div>
					}
				</div>
			</div>
		</div>
	)
}

export default Index
